import _ from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import { VersionHeader } from "src/ui/shared/table/headers/VersionHeader";

import { GroupFilterHeader } from "@dashboard/shared/components/GroupFilterHeader/GroupFilterHeader";
import { getBadgeContent } from "@dashboard/shared/Filtering.utils";
import { TableRow } from "@mui/material";
import { FilterHeader } from "@shared/table/headers/FilterHeader";
import { MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { Header } from "@shared/table/TableHeader";
import { ColumnFiltersState, Table } from "@tanstack/react-table";

import {
    DeviceFilterType,
    getInitialCodenameFilters,
    getInitialLicenseStatusFilters,
    getInitialOnlineStatusFilters,
    getInitialSystemVersionFilters,
    getInitialWarningsFilters,
} from "./filters/DeviceTableFilters.utils";
import { DeviceTableData } from "./DeviceTable.utils";
import { SelectAllLicensesCheckbox } from "./SelectAllLicensesCheckbox";

interface DeviceTableFilterProps {
    table: Table<DeviceTableData>;
    columnFilters: ColumnFiltersState;
    setFilters: (id: DeviceFilterType, values: Record<string, boolean>) => void;
}

export const DeviceTableHeaders: React.FC<DeviceTableFilterProps> = ({ table, columnFilters, setFilters }) => {
    const { t } = useTranslation(["pages", "common"]);
    const systemVersions = React.useMemo(() => {
        return _.uniq(_.compact(table.getCoreRowModel().rows.map((row) => row.original.systemVersion)));
    }, [table]);

    return (
        <MinimalTableHeader>
            {table
                .getHeaderGroups()
                .slice(1)
                .map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => {
                            if (header.id === "moveLicenseCheckbox") {
                                return <SelectAllLicensesCheckbox key={header.id} header={header} id={header.id} table={table} />;
                            }
                            if (header.id === "groupName") {
                                return (
                                    <GroupFilterHeader
                                        key={header.id}
                                        header={header}
                                        table={table}
                                        badgeContent={getBadgeContent("groupName", columnFilters)}
                                        onSubmit={(values) => setFilters("groupName", values)}
                                    />
                                );
                            }
                            if (header.id === "licenseStatus") {
                                return (
                                    <Header key={header.id} {...header}>
                                        <FilterHeader
                                            title={t("pages:devicePage.table.filter.licenseStatus")}
                                            sortDisabled
                                            options={[
                                                { key: "Used", label: t("common:status.used") },
                                                { key: "Unused", label: t("common:status.unused") },
                                            ]}
                                            initialValues={getInitialLicenseStatusFilters(table)}
                                            onSubmit={(values) => setFilters("licenseStatus", values)}
                                        />
                                    </Header>
                                );
                            }
                            if (header.id === "onlineStatus") {
                                return (
                                    <Header key={header.id} {...header}>
                                        <FilterHeader
                                            title={t("pages:devicePage.table.filter.status")}
                                            sortDisabled
                                            options={[
                                                { key: "Online", label: t("pages:devicePage.status.deviceOnline") },
                                                { key: "Offline", label: t("pages:devicePage.status.deviceOffline") },
                                            ]}
                                            initialValues={getInitialOnlineStatusFilters(table)}
                                            onSubmit={(values) => setFilters("onlineStatus", values)}
                                        />
                                    </Header>
                                );
                            }
                            if (header.id === "warnings") {
                                return (
                                    <Header key={header.id} {...header}>
                                        <FilterHeader
                                            title="health"
                                            sortDisabled
                                            options={[
                                                { key: "OK", label: t("pages:devicePage.table.health.ok") },
                                                { key: "Warning", label: t("pages:devicePage.table.health.warning") },
                                            ]}
                                            initialValues={getInitialWarningsFilters(table)}
                                            onSubmit={(values) => setFilters("warnings", values)}
                                        />
                                    </Header>
                                );
                            }
                            if (header.id === "systemVersion") {
                                return (
                                    <Header key={header.id} {...header}>
                                        <VersionHeader
                                            title={t("pages:devicePage.table.filter.systemVersion")}
                                            options={systemVersions.map((version) => ({
                                                key: version,
                                                label: version,
                                            }))}
                                            initialValues={getInitialSystemVersionFilters(table)}
                                            onSubmit={(values) => setFilters("systemVersion", values)}
                                        />
                                    </Header>
                                );
                            }
                            if (header.id === "codename") {
                                const codenames = _.uniq(
                                    _.compact(table.getCoreRowModel().rows.map((row) => row.original.codename)),
                                );
                                return (
                                    <Header key={header.id} {...header}>
                                        <FilterHeader
                                            title={t("pages:devicePage.table.filter.codename")}
                                            options={codenames.map((codename) => ({
                                                key: codename,
                                                label: codename,
                                            }))}
                                            initialValues={getInitialCodenameFilters(table)}
                                            onSubmit={(values) => setFilters("codename", values)}
                                        />
                                    </Header>
                                );
                            } else {
                                return <Header key={header.id} {...header} />;
                            }
                        })}
                    </TableRow>
                ))}
        </MinimalTableHeader>
    );
};
